import { Agent } from './agent';
import { Attachment } from './attachment';
import { Context } from './context';
import { Group } from './group';
import { IActor } from '../resources/istatement/actor';
import { IContext } from '../resources/istatement/context';
import { IGroup } from '../resources/istatement/group';
import { IResult } from '../resources/istatement/result';
import { IStatement } from '../resources/istatement/statement';
import { IVerb } from '../resources/istatement/verb';
import { Result } from './result';
import { StatementRef } from './statement_ref';
import { SubStatement } from './sub_statement';
import { Utils } from '../utils';
import { Verb } from './verb';
import { Versions } from '../constants/versions';
import { IStatementRef } from '../resources/istatement/statement_ref';
import { IStatementObject } from '../resources/istatement/statement_object';
import { Activity, ActivityParams } from './activity';
import { IActivity } from '../resources/activities/activity';

export interface StatementParams {
	id?: string;
	actor?: Agent | Group | IActor | IGroup;
	verb?: Verb | IVerb;
	target?: Activity | Agent | Group | StatementRef | SubStatement | IActivity | IActor | IGroup | IStatementRef;
	object?: Activity | Agent | Group | StatementRef | SubStatement | IActivity | IActor | IGroup | IStatementRef;
	timestamp?: string;
	stored?: string;
	authority?: Agent | Group | IActor | IGroup;
	attachments?: Attachment[];
	context?: Context | IContext;
	result?: Result | IResult;
	version?: Versions;
}

export interface StatementInitCfg {
	storeOriginal: number;
	doStamp: boolean;
}

export class Statement {
	id?: string;
	timestamp?: string;
	stored?: string;
	version?: Versions;
	originalJson?: string;
	objectType = 'Statement';
	private _actor: Agent | Group | undefined;
	private _authority: Agent | Group | undefined;
	private _verb: Verb | undefined;
	private _context: Context | undefined;
	private _result: Result | undefined;
	private _target: Activity | Agent | Group | StatementRef | SubStatement | undefined;
	attachments?: Attachment[];

	constructor(cfg: StatementParams, initCfg: StatementInitCfg = { storeOriginal: 2, doStamp: true }) {
		const { storeOriginal, doStamp } = initCfg;

		if (typeof cfg === 'string') {
			const sp = this.fromJSON(cfg);
			if (storeOriginal) {
				sp.originalJson = JSON.stringify(cfg, undefined, storeOriginal);
			}
			if (doStamp) {
				sp.stamp();
			}
			return sp;
		}

		if (storeOriginal) {
			this.originalJson = JSON.stringify(cfg, undefined, storeOriginal);
		}

		this.actor = this.agent_group(cfg.actor);
		this.authority = this.agent_group(cfg.authority);
		this.verb = this.fix_verb(cfg.verb);
		this.target = this.fix_target(cfg.object ? cfg.object : cfg.target);
		this.result = this.fix_result(cfg.result);
		this.context = this.fix_context(cfg.context);

		if (cfg.attachments) {
			this.attachments = cfg.attachments.map((att) => {
				if (att instanceof Attachment) {
					return att;
				}
				return new Attachment(att);
			});
		}

		this.id = cfg.id;
		this.stored = cfg.stored;
		this.timestamp = cfg.timestamp;
		this.version = cfg.version || '1.0.3';

		if (doStamp) {
			this.stamp();
		}
	}

	private agent_group(value: Agent | Group | IActor | IGroup | undefined): Agent | Group | undefined {
		if (value === undefined) {
			return undefined;
		}
		if (value.objectType === undefined || value.objectType === 'Person') {
			value.objectType = 'Agent';
		}

		if (value.objectType === 'Agent') {
			if (value instanceof Agent) {
				return value;
			} else {
				return new Agent(value);
			}
		} else if (value.objectType === 'Group') {
			if (value instanceof Group) {
				return value;
			} else {
				return new Group(value);
			}
		}
	}

	public get actor(): Agent | Group | undefined {
		return this._actor;
	}
	public set actor(value: Agent | Group | undefined) {
		this._actor = this.agent_group(value);
	}

	public get authority(): Agent | Group | undefined {
		return this._authority;
	}
	public set authority(value: Agent | Group | undefined) {
		this._authority = this.agent_group(value);
	}

	private fix_verb(value: Verb | IVerb | undefined): Verb | undefined {
		return value && (value instanceof Verb ? value : new Verb(value));
	}
	public get verb(): Verb | undefined {
		return this._verb;
	}
	public set verb(value: Verb | undefined) {
		this._verb = value;
	}

	private fix_context(value: Context | IContext | undefined): Context | undefined {
		return value && (value instanceof Context ? value : new Context(value));
	}
	public get context(): Context | undefined {
		return this._context;
	}
	public set context(value: Context | undefined) {
		this._context = value;
	}

	private fix_result(value: Result | IResult | undefined): Result | undefined {
		return value && (value instanceof Result ? value : new Result(value));
	}
	public get result(): Result | undefined {
		return this._result;
	}
	public set result(value: Result | undefined) {
		this._result = value;
	}

	private fix_target(
		value: IStatementObject | undefined
	): Activity | Agent | Group | SubStatement | StatementRef | undefined {
		if (value === undefined) {
			return undefined;
		}
		let target: Activity | Agent | Group | SubStatement | StatementRef | undefined = undefined;

		if (
			value instanceof Activity ||
			value instanceof Agent ||
			value instanceof Group ||
			value instanceof SubStatement ||
			value instanceof StatementRef
		) {
			target = value;
		} else {
			if (typeof value.objectType === 'undefined') {
				value.objectType = 'Activity';
			}

			if (value.objectType === 'Activity') {
				target = new Activity(value as ActivityParams);
			} else if (value.objectType === 'Agent') {
				target = new Agent(value);
			} else if (value.objectType === 'Group') {
				target = new Group(value);
			} else if (value.objectType === 'SubStatement') {
				target = new SubStatement(value as any);
			} else if (value.objectType === 'StatementRef') {
				target = new StatementRef(value);
			}
		}

		return target;
	}
	public get target(): Activity | Agent | Group | SubStatement | StatementRef | undefined {
		return this._target;
	}
	public set target(value: Activity | Agent | Group | SubStatement | StatementRef | undefined) {
		this._target = value;
	}

	toString(lang: string = 'en-US'): string {
		return `${this.actor !== undefined ? this.actor.toString() : ''} ${
			this.verb !== undefined ? this.verb.toString(lang) : ''
		} ${this.target !== undefined ? this.target.toString(lang) : ''}`;
	}

	asVersion(version: Versions = '1.0.3'): IStatement {
		const { id, timestamp, actor, verb, result, context, authority, target } = this;
		const stmnt: Partial<IStatement> = {
			id,
			timestamp,
			actor: actor?.asVersion(),
			verb: verb?.asVersion(),
			result: result?.asVersion(),
			context: context?.asVersion(),
			authority: authority?.asVersion(),
			object: target?.asVersion()
		};

		if (this.attachments) {
			stmnt.attachments = this.attachments.map((att) => {
				if (att instanceof Attachment) {
					return att.asVersion(version);
				}
				return new Attachment(att).asVersion(version);
			});
		}

		return stmnt as IStatement;
	}

	stamp(): void {
		if (!this.id) {
			this.id = Utils.getUUID();
		}
		if (!this.timestamp) {
			this.timestamp = Utils.isoNow;
		}
	}

	/**
	Checks if the Statement has at least one attachment with content
	*/
	hasAttachmentWithContent(): boolean {
		if (!this.attachments) {
			return false;
		}

		for (let i = 0; i < this.attachments.length; i += 1) {
			if (this.attachments[i].content !== null) {
				return true;
			}
		}

		return false;
	}

	fromJSON(stJSON: string): Statement {
		const _st = JSON.parse(stJSON);

		return new Statement(_st);
	}
}
